/**
 * Admin panel: invites, people, room access, and task categories.
 *
 * Only rendered for admins, but that is presentation only — every endpoint it
 * calls checks the caller's role server-side. Hiding a button is not security.
 */

import type { Role } from '@virtual-office/shared';

import * as api from '../api.js';
import { $, clear, el, relativeTime } from './dom.js';
import { toast } from './toasts.js';

type Tab = 'invites' | 'people' | 'rooms' | 'categories';

export class AdminPanel {
  private readonly panel = $('admin');
  private readonly tabs = $('admin-tabs');
  private readonly body = $('admin-body');
  private tab: Tab = 'invites';

  constructor(private readonly onAccessChanged: () => void) {
    $('admin-close').addEventListener('click', () => this.close());
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  open(): void {
    this.panel.hidden = false;
    this.renderTabs();
    void this.renderBody();
  }

  close(): void {
    this.panel.hidden = true;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  private renderTabs(): void {
    clear(this.tabs);
    const labels: Array<[Tab, string]> = [
      ['invites', 'Invites'],
      ['people', 'People'],
      ['rooms', 'Rooms'],
      ['categories', 'Categories'],
    ];
    for (const [id, label] of labels) {
      const button = el('button', { className: 'board-tab', text: label });
      button.type = 'button';
      if (id === this.tab) button.classList.add('selected');
      button.addEventListener('click', () => {
        this.tab = id;
        this.renderTabs();
        void this.renderBody();
      });
      this.tabs.append(button);
    }
  }

  private async renderBody(): Promise<void> {
    clear(this.body);
    this.body.append(el('p', { className: 'empty', text: 'Loading…' }));
    try {
      if (this.tab === 'invites') await this.renderInvites();
      else if (this.tab === 'people') await this.renderPeople();
      else if (this.tab === 'categories') await this.renderCategories();
      else await this.renderRooms();
    } catch (error) {
      clear(this.body);
      this.body.append(
        el('p', {
          className: 'error',
          text: error instanceof Error ? error.message : 'Could not load that.',
        }),
      );
    }
  }

  /* --- invites ----------------------------------------------------------- */

  private async renderInvites(): Promise<void> {
    const [{ invites, emailEnabled }, { groups }] = await Promise.all([
      api.listInvites(),
      api.listGroups(),
    ]);
    clear(this.body);

    const email = el('input');
    email.type = 'email';
    email.placeholder = 'their@email.com';
    email.required = true;

    const role = el('select');
    for (const [value, label] of [
      ['member', 'Member'],
      ['admin', 'Admin'],
    ]) {
      const option = el('option', { text: label });
      option.value = value ?? 'member';
      role.append(option);
    }

    const send = el('button', { text: 'Send invite' });
    send.type = 'submit';

    // Declared before the form so the submit handler closing over it reads in
    // order; the chips that fill it are rendered below the form.
    const picked = new Set<string>();

    const form = el('form', { className: 'admin-form' }, [email, role, send]);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void (async () => {
        send.disabled = true;
        try {
          const result = await api.sendInvite(email.value.trim(), role.value as Role, [
            ...picked,
          ]);
          // Always surface the link: email may be off, or may silently fail.
          if (result.emailed) toast(`Invite emailed to ${email.value.trim()}`);
          else if (result.invite.url) {
            await navigator.clipboard?.writeText(result.invite.url).catch(() => {});
            toast('Email is off — invite link copied to your clipboard');
          }
          email.value = '';
          await this.renderBody();
        } catch (error) {
          toast(error instanceof Error ? error.message : 'Could not send that.', 'error');
        } finally {
          send.disabled = false;
        }
      })();
    });

    this.body.append(form);
    // Departments and teams chosen now are applied the moment they accept, so
    // the org chart is right from their first minute rather than a follow-up
    // task somebody forgets.
    const assignable = groups.filter((g) => g.ownerId === null && g.kind !== 'tag');
    if (assignable.length > 0) {
      const chips = el('div', { className: 'room-members' });
      for (const group of assignable) {
        const chip = el('button', { className: 'member-chip', text: group.name });
        chip.type = 'button';
        chip.addEventListener('click', () => {
          if (picked.has(group.id)) picked.delete(group.id);
          else picked.add(group.id);
          chip.classList.toggle('allowed', picked.has(group.id));
        });
        chips.append(chip);
      }
      this.body.append(
        el('p', { className: 'option-label', text: 'Put them in' }),
        chips,
      );
    }
    if (!emailEnabled) {
      this.body.append(
        el('p', {
          className: 'hint-inline',
          text: 'Email is not configured, so invite links are copied to your clipboard instead.',
        }),
      );
    }

    if (invites.length === 0) {
      this.body.append(el('p', { className: 'empty', text: 'No invites yet.' }));
      return;
    }

    for (const invite of invites) {
      const status = invite.acceptedAt
        ? `joined ${relativeTime(invite.acceptedAt)}`
        : invite.expiresAt < Date.now()
          ? 'expired'
          : `pending · expires ${relativeTime(invite.expiresAt)}`;

      const row = el('div', { className: 'admin-row' }, [
        el('div', { className: 'admin-row-main' }, [
          el('span', { className: 'admin-row-title', text: invite.email }),
          el('span', { className: 'admin-row-detail', text: `${invite.role} · ${status}` }),
        ]),
      ]);

      if (!invite.acceptedAt) {
        const revoke = el('button', { className: 'ghost small danger', text: 'Revoke' });
        revoke.type = 'button';
        revoke.addEventListener('click', () => {
          void (async () => {
            await api.revokeInvite(invite.id);
            await this.renderBody();
          })();
        });
        row.append(revoke);
      }
      this.body.append(row);
    }
  }

  /* --- people ------------------------------------------------------------ */

  private async renderPeople(): Promise<void> {
    const { accounts } = await api.listAccounts();
    clear(this.body);

    for (const account of accounts) {
      const role = el('select', { className: 'admin-role' });
      for (const [value, label] of [
        ['member', 'Member'],
        ['admin', 'Admin'],
      ]) {
        const option = el('option', { text: label });
        option.value = value ?? 'member';
        if (value === account.role) option.selected = true;
        role.append(option);
      }
      role.addEventListener('change', () => {
        void (async () => {
          try {
            await api.setAccountRole(account.id, role.value as Role);
            this.onAccessChanged();
            toast(`${account.name} is now ${role.value}`);
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not change that.', 'error');
            await this.renderBody();
          }
        })();
      });

      const signOut = el('button', { className: 'ghost small', text: 'Sign out' });
      signOut.type = 'button';
      signOut.title = 'End all their sessions';
      signOut.addEventListener('click', () => {
        void (async () => {
          await api.signOutAccount(account.id);
          toast(`Signed ${account.name} out everywhere`);
        })();
      });

      this.body.append(
        el('div', { className: 'admin-row' }, [
          el('div', { className: 'admin-row-main' }, [
            el('span', { className: 'admin-row-title', text: account.name }),
            el('span', { className: 'admin-row-detail', text: account.email }),
          ]),
          role,
          signOut,
        ]),
      );
    }
  }

  /* --- rooms ------------------------------------------------------------- */

  private async renderRooms(): Promise<void> {
    const { rooms, accounts } = await api.listRooms();
    clear(this.body);

    for (const room of rooms) {
      const isCommons = room.kind === 'commons';

      const minRole = el('select', { className: 'admin-role' });
      for (const [value, label] of [
        ['member', 'Anyone'],
        ['admin', 'Admins only'],
      ]) {
        const option = el('option', { text: label });
        option.value = value ?? 'member';
        if (value === room.minRole) option.selected = true;
        minRole.append(option);
      }

      const restricted = el('input');
      restricted.type = 'checkbox';
      restricted.checked = room.restricted;

      const savePolicy = () => {
        void (async () => {
          try {
            await api.setRoomPolicy(room.zoneId, minRole.value as Role, restricted.checked);
            this.onAccessChanged();
            await this.renderBody();
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not save.', 'error');
            await this.renderBody();
          }
        })();
      };
      minRole.addEventListener('change', savePolicy);
      restricted.addEventListener('change', savePolicy);

      if (isCommons) {
        minRole.disabled = true;
        restricted.disabled = true;
      }

      const controls = el('div', { className: 'room-controls' }, [
        minRole,
        el('label', { className: 'checkbox' }, [
          restricted,
          el('span', { text: 'Allowlist only' }),
        ]),
      ]);

      const card = el('div', { className: 'admin-room' }, [
        el('div', { className: 'admin-row-main' }, [
          el('span', { className: 'admin-row-title', text: room.name }),
          el('span', {
            className: 'admin-row-detail',
            text: isCommons ? 'Always open — it is how people get around' : room.kind,
          }),
        ]),
        controls,
      ]);

      // The allowlist only matters when it is switched on.
      if (room.restricted && !isCommons) {
        const members = el('div', { className: 'room-members' });
        for (const account of accounts) {
          const allowed = room.members.includes(account.id) || account.role === 'admin';
          const chip = el('button', {
            className: 'member-chip',
            text: account.name,
            title: account.role === 'admin' ? 'Admins always have access' : account.email,
          });
          chip.type = 'button';
          if (allowed) chip.classList.add('allowed');
          if (account.role === 'admin') {
            chip.disabled = true;
          } else {
            chip.addEventListener('click', () => {
              void (async () => {
                await api.setRoomMember(
                  room.zoneId,
                  account.id,
                  !room.members.includes(account.id),
                );
                this.onAccessChanged();
                await this.renderBody();
              })();
            });
          }
          members.append(chip);
        }
        card.append(members);
      }

      this.body.append(card);
    }
  }

  /* --- task categories ---------------------------------------------------- */

  /**
   * The categories tasks can be filed under.
   *
   * Admin-managed rather than free text: three people typing "Design",
   * "design" and "desgin" give you three categories and a board nobody can
   * group by. The cost is that a member meeting something genuinely new has
   * to ask, which is the trade you picked.
   */
  private async renderCategories(): Promise<void> {
    const { categories } = await api.listCategories();
    clear(this.body);

    const name = el('input');
    name.placeholder = 'Design';
    name.maxLength = 40;
    name.required = true;

    const add = el('button', { text: 'Add' });
    add.type = 'submit';

    const form = el('form', { className: 'admin-form two-up' }, [name, add]);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void (async () => {
        add.disabled = true;
        try {
          await api.createCategory(name.value.trim());
          name.value = '';
          await this.renderBody();
        } catch (error) {
          toast(error instanceof Error ? error.message : 'Could not add that.', 'error');
        } finally {
          add.disabled = false;
        }
      })();
    });
    this.body.append(form);

    if (categories.length === 0) {
      this.body.append(
        el('p', {
          className: 'empty',
          text: 'No categories yet. Until you add one, tasks stay uncategorised.',
        }),
      );
      return;
    }

    for (const category of categories) {
      const card = el('div', { className: 'admin-room' });
      if (category.archived) card.classList.add('is-archived');

      const used =
        category.taskCount === 0
          ? 'Unused'
          : `${category.taskCount} ${category.taskCount === 1 ? 'task' : 'tasks'}`;

      const head = el('div', { className: 'group-head' }, [
        el('div', { className: 'admin-row-main' }, [
          el('span', { className: 'admin-row-title', text: category.name }),
          el('span', {
            className: 'admin-row-detail',
            text: category.archived ? `${used} · retired` : used,
          }),
        ]),
      ]);

      const rename = el('button', { className: 'ghost small', text: 'Rename' });
      rename.type = 'button';
      rename.addEventListener('click', () => {
        void (async () => {
          const next = prompt('Rename this category to:', category.name);
          if (next === null || next.trim() === '' || next.trim() === category.name) return;
          try {
            await api.renameCategory(category.id, next.trim());
            await this.renderBody();
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not rename.', 'error');
          }
        })();
      });

      // Retiring stops a category being offered without touching the tasks
      // already filed under it, which is almost always what is wanted.
      const archive = el('button', {
        className: 'ghost small',
        text: category.archived ? 'Bring back' : 'Retire',
      });
      archive.type = 'button';
      archive.addEventListener('click', () => {
        void (async () => {
          try {
            await api.archiveCategory(category.id, !category.archived);
            await this.renderBody();
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not change that.', 'error');
          }
        })();
      });

      head.append(rename, archive);

      // Deleting is only offered for a category nothing uses; the server
      // refuses the rest and says to retire it instead.
      if (category.taskCount === 0) {
        const remove = el('button', { className: 'ghost small danger', text: 'Delete' });
        remove.type = 'button';
        remove.addEventListener('click', () => {
          void (async () => {
            if (!confirm(`Delete the "${category.name}" category?`)) return;
            try {
              await api.deleteCategory(category.id);
              await this.renderBody();
            } catch (error) {
              toast(error instanceof Error ? error.message : 'Could not delete.', 'error');
            }
          })();
        });
        head.append(remove);
      }

      card.append(head);
      this.body.append(card);
    }
  }
}
